import { Field, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import getValidator from '../../../utils/getValidator';
import SubHeadline from '../../atoms/SubHeadline';
import ToolTip from '../../atoms/ToolTip';
import InputWithIconAndButtonField from '../../formik/InputWithIconAndButtonField';
import ProgramRegistration from './ProgramRegistration';

type ProgramActivationProps = {
  onSignIn: (loyaltyProgramCode: string) => Promise<void>;
  onRegister: () => Promise<void>;
};

const ProgramActivation = ({ onSignIn, onRegister }: ProgramActivationProps) => {
  const { formatMessage } = useIntl();
  const { checkoutState, isLoading } = useCheckoutState();
  const customerHasBillingAddress = !!checkoutState.orderState.addresses?.billing;

  const programConfig = config.features.loyaltyProgram;
  const isRegistrationDisabled = !customerHasBillingAddress || !!isLoading;

  const initialValues = {
    loyaltyProgramCode: '',
    isTermsAndConditionsAccepted: false
  };

  const icon = { src: getAssetUrl('inputs/loyaltyProgramActive.svg') };
  const button = { text: 'generic.add', dataTestId: 'payment-add-payback' };
  const earnings = checkoutState.orderState.memberships?.suggestions[0]?.earnings.points;

  return (
    <Formik onSubmit={({ loyaltyProgramCode }) => onSignIn(loyaltyProgramCode)} initialValues={initialValues}>
      {({ submitForm, values }) => (
        <>
          {programConfig.registration.enabled && (
            <ProgramRegistration
              onRegister={onRegister}
              isLoading={isLoading === LOADING_TYPES.loyaltyRegister}
              isDisabled={isRegistrationDisabled || !values.isTermsAndConditionsAccepted}
            />
          )}
          <div className="loyalty-program__tool-tip">
            <SubHeadline>
              <FormattedMessage id="payment.loyaltyProgram.headline" />
            </SubHeadline>
            {programConfig.toolTip.enabled && (
              <ToolTip
                content={formatMessage({ id: 'payment.loyaltyProgram.toolTip' })}
                dataTestId="loyalty-program-activation"
              />
            )}
          </div>
          {config.session.altLoyaltyId ?
            <div className="loyalty-program__alt-loyalty-id">
              <img src={getAssetUrl('inputs/loyaltyProgramActive.svg')} />
              <FormattedMessage id="payment.loyaltyProgram.altLoyaltyIdToolTip" />
            </div>
          : <Field
              component={InputWithIconAndButtonField}
              icon={programConfig.icon.enabled ? icon : undefined}
              button={{ ...button, disabled: !!isLoading, onClick: submitForm }}
              placeholder={formatMessage({ id: 'payment.loyaltyProgram.label' })}
              name="loyaltyProgramCode"
              isLoading={isLoading === LOADING_TYPES.loyaltyAdd}
              validate={getValidator(programConfig)}
              maxLength={programConfig.maxLength}
              formatOnBlur={programConfig.formatOnBlur}
              isInputStatusIconDisabled
              data-test-id="loyalty-program"
            />
          }
          <p className="loyalty-program__earnings">
            <FormattedMessage id="payment.loyaltyProgram.legend" values={{ earnings }} />
          </p>
        </>
      )}
    </Formik>
  );
};

export default ProgramActivation;
